import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ArrowRight, Eye, EyeOff, Lock, User } from 'lucide-react';
import { zodResolver } from '@/lib/zodResolver';
import { landingRouteFor, useAuthStore } from '@/store/authStore';
import { ApiError } from '@/types';
import { Button } from '@/components/ui/Button';
import { Checkbox, Field, Input } from '@/components/ui/Field';
import { Alert, FullPageLoader } from '@/components/ui/Feedback';

const schema = z.object({
  email: z.string().min(1, 'Username is required.').email('Enter a valid username / email address.'),
  password: z.string().min(1, 'Password is required.'),
  rememberMe: z.boolean(),
});

type LoginForm = z.infer<typeof schema>;

export function LoginPage() {
  const login = useAuthStore((state) => state.login);
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // Stays true from a successful login until the route actually swaps, so the
  // gap where the dashboard chunk is still downloading is covered rather than
  // showing a dead form with a spinning button.
  const [signingIn, setSigningIn] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver<typeof schema, LoginForm>(schema),
    defaultValues: { email: '', password: '', rememberMe: true },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      const user = await login(values);
      setSigningIn(true);
      // Send the user back where they were headed, or to their role's home.
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from ?? landingRouteFor(user), { replace: true });
    } catch (error) {
      setSigningIn(false);
      if (error instanceof ApiError && error.fieldErrors) {
        for (const [field, message] of Object.entries(error.fieldErrors)) {
          setError(field as keyof LoginForm, { message });
        }
        return;
      }
      setFormError(error instanceof Error ? error.message : 'Sign in failed. Please try again.');
    }
  });

  return (
    <div className="grid h-screen lg:grid-cols-[40%_60%]">
      {signingIn && <FullPageLoader label="Signing you in…" sublabel="Preparing your workspace" />}

      {/* ------------------------------- form -------------------------------- */}
      <main className="relative flex items-center justify-center overflow-hidden bg-gradient-to-b from-brand-50/70 via-white to-brand-50/40 px-5 py-10 sm:px-8">
        {/* corner dot-grid, echoing the mockup */}
        <svg className="pointer-events-none absolute top-6 left-6 size-24 text-brand-200" aria-hidden>
          <pattern id="dots-tl" width="10" height="10" patternUnits="userSpaceOnUse">
            <circle cx="1.5" cy="1.5" r="1.5" fill="currentColor" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#dots-tl)" />
        </svg>
        <svg className="pointer-events-none absolute bottom-6 left-6 size-24 text-brand-200" aria-hidden>
          <pattern id="dots-bl" width="10" height="10" patternUnits="userSpaceOnUse">
            <circle cx="1.5" cy="1.5" r="1.5" fill="currentColor" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#dots-bl)" />
        </svg>
        <span className="pointer-events-none absolute top-1/3 -left-16 size-64 rounded-full bg-brand-100/60 blur-3xl" aria-hidden />

        <div className="relative w-full max-w-[400px]">
          <div className="mb-8 flex flex-col items-center text-center">
            <img 
              src="/asset/image/logo.png" 
              alt="BD Design Pvt. Ltd." 
              className="size-40 object-contain" 
            />
            <h1 className="text-3xl font-extrabold tracking-tight text-navy-900">BD Design Pvt. Ltd.</h1>
            <p className="mt-1 text-m font-semibold text-brand-600">Carton QR Tracking System</p>
          </div>

          <div className="rounded-2xl border border-line bg-white p-6 shadow-pop sm:p-8">
            <h2 className="text-xl font-bold tracking-tight text-ink">Welcome Back</h2>
            <p className="mt-1 text-sm text-ink-muted">Sign in to continue to your account</p>

            {formError && (
              <Alert tone="error" className="mt-5">
                {formError}
              </Alert>
            )}

            <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4" noValidate>
              <Field label="Username" htmlFor="email" required error={errors.email?.message}>
                <Input
                  id="email"
                  type="text"
                  autoComplete="username"
                  placeholder="Enter your username"
                  leftIcon={<User className="size-4" aria-hidden />}
                  error={errors.email?.message}
                  {...register('email')}
                />
              </Field>

              <Field label="Password" htmlFor="password" required error={errors.password?.message}>
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  leftIcon={<Lock className="size-4" aria-hidden />}
                  error={errors.password?.message}
                  rightSlot={
                    <button
                      type="button"
                      onClick={() => setShowPassword((value) => !value)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="inline-flex size-7 items-center justify-center rounded-md hover:bg-canvas"
                    >
                      {showPassword ? <EyeOff className="size-4" aria-hidden /> : <Eye className="size-4" aria-hidden />}
                    </button>
                  }
                  {...register('password')}
                />
              </Field>

              <div className="flex items-center justify-between">
                <Checkbox label="Remember Me" {...register('rememberMe')} />
                <Link to="/login" className="text-[13px] font-semibold text-brand-600 hover:underline">
                  Forgot Password?
                </Link>
              </div>

              <Button
                type="submit"
                size="lg"
                fullWidth
                loading={isSubmitting}
                rightIcon={!isSubmitting ? <ArrowRight className="size-4" aria-hidden /> : undefined}
                className="bg-brand-700 hover:bg-brand-800"
              >
                Login
              </Button>
            </form>

            <div className="mt-6 flex items-center gap-3">
              <span className="h-px flex-1 bg-line" />
              <span className="text-xs text-ink-muted">or continue with</span>
              <span className="h-px flex-1 bg-line" />
            </div>
          </div>

          <p className="mt-6 text-center text-sm text-ink-muted">
            New company?{' '}
            <Link to="/register" className="font-semibold text-brand-600 hover:underline">
              Create a workspace
            </Link>
          </p>

          <p className="mt-4 text-center text-xs text-ink-muted">
            {new Date().getFullYear()} © Presented by <span className="font-bold text-ink-soft">Suffix IT Limited</span>
          </p>

          {/* {env.useMocks && (
            <div className="mt-6 rounded-xl border border-line bg-canvas/70 p-4">
              <p className="text-[13px] font-bold text-ink">Demo accounts</p>
              <p className="mt-0.5 text-xs text-ink-muted">
                Running on the mock API — pick a role to fill the form.
              </p>
              <div className="mt-3 flex flex-col gap-2">
                {DEMO_ACCOUNTS.map((account) => (
                  <button
                    key={account.role}
                    type="button"
                    onClick={() => fillDemo(account)}
                    className="flex items-center justify-between rounded-lg border border-line bg-white px-3 py-2 text-left transition-colors hover:border-brand-300 hover:bg-brand-50/50"
                  >
                    <span>
                      <span className="block text-[13px] font-semibold text-ink">
                        {ROLE_LABELS[account.role]}
                      </span>
                      <span className="block text-[11px] text-ink-muted">{account.email}</span>
                    </span>
                    <span className="text-[11px] font-semibold text-brand-600">Use</span>
                  </button>
                ))}
              </div>
            </div>
          )} */}
        </div>
      </main>

      {/* ------------------------------ illustration --------------------------- */}
      <aside className="hidden overflow-hidden bg-gradient-to-br from-brand-100 via-brand-50 to-white lg:block">
        <div className="flex h-full w-full items-center justify-center">
          <img 
            src="/asset/image/warehouse.png" 
            alt="Warehouse Illustration" 
            className="h-full w-full object-cover" 
          />
        </div>
      </aside>
    </div>
  );
}